import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import heroImage from "@/assets/hero-ai-voice.jpg";
import ParticleBackground from "./ParticleBackground";
import AIGrid from "./AIGrid";
import { motion } from "framer-motion";
import StatsCounter from "./StatsCounter";
import { useTranslation } from 'react-i18next';

const Hero = () => {
  const { t } = useTranslation();
  
  const stats = [
    { value: "40", label: t('hero.stat_cost'), suffix: "%" },
    { value: "99.9", label: t('hero.stat_uptime'), suffix: "%" },
    { value: "∞", label: t('hero.stat_concurrent') },
    { value: "50", label: t('hero.stat_response'), suffix: "ms" },
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628]/95 via-[#0F1B2D]/90 to-[#1a0b2e]/95" />
      </div>

      {/* Animated Background Effects */}
      <ParticleBackground />
      <AIGrid />

      {/* Content */}
      <div className="container mx-auto px-4 py-32 relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <div className="inline-block px-4 py-2 rounded-full glass-effect border border-accent/20 mb-6">
              <span className="text-accent font-semibold text-sm">{t('hero.badge')}</span>
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-5xl md:text-7xl lg:text-8xl font-bold mb-8 text-white leading-tight"
            dangerouslySetInnerHTML={{ __html: t('hero.headline') }}
          />
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl mb-12 text-white/80 max-w-4xl mx-auto leading-relaxed"
          >
            {t('hero.description')}
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-20"
          >
            <Link to="/contact">
              <Button size="lg" className="gradient-brand text-white hover:opacity-90 text-lg px-10 py-7 rounded-xl gradient-glow">
                {t('hero.cta_primary')}
              </Button>
            </Link>
            <Link to="/platform">
              <Button size="lg" variant="outline" className="text-lg px-10 py-7 border-2 border-white/20 text-white hover:bg-white/10 rounded-xl backdrop-blur-sm">
                {t('hero.cta_secondary')}
              </Button>
            </Link>
          </motion.div>

          {/* Stats Counter */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <StatsCounter stats={stats} />
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2"
        >
          <motion.div
            animate={{ height: ["20%", "80%", "20%"] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 bg-white/50 rounded-full"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
